'use client';

import React, { ErrorInfo, ReactNode } from 'react';
import * as Sentry from '@sentry/nextjs';
import { captureException } from '@/lib/posthog-tracking';
import { Button } from '@shared/components/shadcn/button';
import { AlertCircle, RefreshCw } from 'lucide-react';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  context?: {
    assessmentType?: string;
    userEmail?: string;
    step?: string | number;
  };
}

interface State {
  hasError: boolean;
  error?: Error;
}

export class ErrorBoundary extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Log error to Sentry (replaces functionality from removed global-error.tsx)
    Sentry.captureException(error, {
      contexts: {
        react: {
          componentStack: errorInfo.componentStack,
        },
      },
      tags: {
        source: 'react_component',
        assessmentType: this.props.context?.assessmentType,
        step: String(this.props.context?.step),
      },
      user: {
        email: this.props.context?.userEmail,
      },
    });

    // Log error to PostHog with full context
    captureException(error, {
      source: 'react_component',
      severity: 'high',
      assessmentType: this.props.context?.assessmentType,
      userEmail: this.props.context?.userEmail,
      step: this.props.context?.step,
      additionalData: {
        component_stack: errorInfo.componentStack,
        error_boundary: true,
        props: this.props.context,
      },
    });

    // Also log to console in development
    if (process.env.NODE_ENV === 'development') {
      console.error('Error caught by boundary:', error, errorInfo);
    }
  }

  handleReset = () => {
    this.setState({ hasError: false, error: undefined });
  };

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      // If custom fallback provided, use it
      if (this.props.fallback) {
        return <>{this.props.fallback}</>;
      }

      // Default error UI
      return (
        <div className="flex min-h-[400px] flex-col items-center justify-center p-8">
          <div className="max-w-md text-center space-y-4">
            <div className="mx-auto w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
              <AlertCircle className="w-6 h-6 text-red-600" />
            </div>
            
            <h2 className="text-xl font-semibold text-gray-900">
              Something went wrong
            </h2>
            
            <p className="text-sm text-gray-600">
              We encountered an unexpected error. The issue has been reported 
              and we&apos;ll look into it. Please try refreshing the page.
            </p>

            {process.env.NODE_ENV === 'development' && this.state.error && (
              <div className="mt-4 p-3 bg-gray-100 rounded-md text-left">
                <p className="text-xs font-mono text-gray-700">
                  {this.state.error.message}
                </p>
              </div>
            )}

            <div className="flex gap-3 justify-center pt-4">
              <Button
                onClick={this.handleReset}
                variant="outline"
                size="sm"
              >
                Try Again
              </Button>
              <Button
                onClick={this.handleReload}
                size="sm"
                className="gap-2"
              >
                <RefreshCw className="w-4 h-4" />
                Refresh Page
              </Button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

/**
 * Hook to use error boundary programmatically
 */
export function useErrorHandler() {
  return (error: Error, context?: Parameters<typeof captureException>[1]) => {
    captureException(error, context || {
      source: 'react_component',
      severity: 'medium',
    });
    
    // Optionally trigger React's error boundary
    throw error;
  };
}